import { FilterMenu } from '@/components/filter-menu';
import { TableControlContainer } from '@/components/table-control-container';
import { SupportAgentOutlined } from '@/constants/hardfin-standard-icons';
import { SortDirection, invert } from '@/constants/sort';
import { getTickets } from '@/ticket/api/get-tickets';
import { TicketCategory, TicketStatus } from '@/ticket/api/types';
import { TicketTableRow } from '@/ticket/components/ticket-table-row';
import { categories } from '@/ticket/util/category';
import { FIVE_MINUTES_MS } from '@/utilities/time-constants';
import {
  Box,
  Card,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';

interface TicketTableProps {
  status: TicketStatus;
}

type SortField =
  | 'title'
  | 'category'
  | 'assignedTo'
  | 'createdAt'
  | 'assets'
  | 'projects';

export function TicketTable({ status }: TicketTableProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedCategories, setSelectedCategories] = useState<
    TicketCategory[]
  >([]);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 300),
    []
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchValue(value); // Update the input value immediately
      debouncedSetSearch(value); // Debounce the search update
    },
    [debouncedSetSearch]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', status, selectedCategories, debouncedSearch],
    queryFn: () =>
      getTickets({
        status,
        category:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        search: debouncedSearch.trim() !== '' ? debouncedSearch : undefined,
      }),
    refetchInterval: FIVE_MINUTES_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const handleCategoryFilterChange = (
    id: TicketCategory,
    isActive: boolean
  ) => {
    if (isActive) {
      setSelectedCategories([...selectedCategories, id]);
    } else {
      setSelectedCategories(selectedCategories.filter((cat) => cat !== id));
    }
  };

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(invert(sortDirection));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const categoryOptions = Object.entries(categories).map(([id, label]) => ({
    id: id as TicketCategory,
    label: label.label,
    isActive: selectedCategories.includes(id as TicketCategory),
  }));

  const sortedTickets = useMemo(() => {
    if (!data?.tickets) return [];

    return [...data.tickets].sort((a, b) => {
      const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'title':
          return directionMultiplier * a.title.localeCompare(b.title);
        case 'category':
          return directionMultiplier * a.category.localeCompare(b.category);
        case 'assignedTo': {
          if (!a.assignedTo && !b.assignedTo) return 0;
          if (!a.assignedTo) return 1;
          if (!b.assignedTo) return -1;
          const aName = [a.assignedTo.firstName, a.assignedTo.lastName]
            .filter(Boolean)
            .join(' ');
          const bName = [b.assignedTo.firstName, b.assignedTo.lastName]
            .filter(Boolean)
            .join(' ');
          return directionMultiplier * aName.localeCompare(bName);
        }
        case 'createdAt':
          return (
            directionMultiplier *
            (a.createdAt.toMillis() - b.createdAt.toMillis())
          );
        case 'assets':
          return directionMultiplier * (a.assets.length - b.assets.length);
        case 'projects':
          return directionMultiplier * (a.projects.length - b.projects.length);
        default:
          return 0;
      }
    });
  }, [data?.tickets, sortField, sortDirection]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading tickets: {String(error)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableControlContainer>
        <Stack direction="row" marginRight="auto" spacing={1}>
          <FilterMenu
            label="Category"
            options={categoryOptions}
            onChangeOption={handleCategoryFilterChange}
          />
        </Stack>
        <TextField
          key="ticket-search"
          size="small"
          label="Search tickets"
          value={searchValue}
          onChange={handleSearchChange}
          inputRef={searchInputRef}
          sx={{ width: { xs: '100%', sm: '20rem' } }}
        />
      </TableControlContainer>
      {!sortedTickets.length ? (
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            textAlign: 'center',
            minHeight: '200px',
          }}
        >
          <SupportAgentOutlined
            sx={{ fontSize: 48, color: 'text.secondary', marginBottom: 2 }}
          />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tickets yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: '400px', marginBottom: 3 }}
          >
            Tickets help you manage support requests
          </Typography>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  onClick={() => handleSortChange('title')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Title
                    {sortField === 'title' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortChange('category')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Category
                    {sortField === 'category' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortChange('assignedTo')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Assigned To
                    {sortField === 'assignedTo' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortChange('createdAt')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Created At
                    {sortField === 'createdAt' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortChange('assets')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Assets
                    {sortField === 'assets' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  onClick={() => handleSortChange('projects')}
                  sx={{ cursor: 'pointer' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Projects
                    {sortField === 'projects' && (
                      <Box component="span" sx={{ ml: 0.5 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTickets.map((ticket, index) => (
                <TicketTableRow
                  key={ticket.id}
                  ticket={ticket}
                  isLastRow={index === sortedTickets.length - 1}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
